import { CSSObject } from '@emotion/react';
import {
    HoneycombTheme,
    useHoneycombTheme,
    createResponsiveStyles,
    ButtonCornerStyle,
    ButtonShape,
    ButtonSize,
    ButtonVariant,
    ResponsiveStyleValue,
    ButtonColor,
    ButtonElementColors,
    mergeResponsiveStyleValues,
} from '../../theming';

import { alpha, darken, isButtonThemeableColor } from '../../utils';

const getBorderRadius = (cornerStyle: ButtonCornerStyle, height: number, radius: number): string | null => {
    switch (cornerStyle) {
        case 'pill':
            return `${height / 2}px`;
        case 'rounded':
            return `${radius}px`;
        default:
            return null;
    }
};

function getSizeStyles(cornerStyle: ButtonCornerStyle, shape: ButtonShape, theme: HoneycombTheme) {
    return (values: { size: ButtonSize; fullWidth: boolean }) => {
        const { size, fullWidth } = values;
        const componentTheme = theme.components.themes.Button;
        const sizeProps = componentTheme.size[size];
        const borderRadius = getBorderRadius(cornerStyle, sizeProps.height, sizeProps.radius);

        const squareOrRound = shape !== 'default';

        return {
            display: fullWidth ? 'flex' : 'inline-flex',
            width: fullWidth ? '100%' : 'auto',

            fontSize: theme.typography.variant.body[sizeProps.fontSize].l.fontSize,
            height: `${sizeProps.height}px`,
            padding: `0 ${sizeProps.paddingX}px`,
            borderRadius: shape === 'round' ? '50%' : borderRadius ?? undefined,
            gap: `${sizeProps.iconClearance}px`,

            '> [data-icon="true"], > [data-loader="true"]': {
                width: `${sizeProps.iconSize}px`,
                height: `${sizeProps.iconSize}px`,
            },

            ...(squareOrRound
                ? {
                      width: `${sizeProps.height}px`,
                      padding: 0,
                  }
                : null),
        };
    };
}

const getElementColors = (color: ButtonColor, theme: HoneycombTheme): ButtonElementColors => {
    const { components } = theme;
    const componentTheme = components.themes.Button;

    if (isButtonThemeableColor(color)) {
        return componentTheme.colors[color];
    }

    return componentTheme.fixedColors[color];
};

const getColorStyles = (variant: ButtonVariant, colors: ButtonElementColors, theme: HoneycombTheme): CSSObject => {
    const resolved = theme.fn.resolveFlatColorObject(colors);
    const { background, border, textFilled, textOutlined } = resolved;

    if (variant === 'outlined') {
        return {
            backgroundColor: 'transparent',
            color: textOutlined,
            borderColor: border,
            '&:not([data-disabled])': theme.fn.hoverStyles({ backgroundColor: alpha(border, 0.1) }),
        };
    }

    return {
        backgroundColor: background,
        color: textFilled,
        borderColor: 'transparent',
        '&:not([data-disabled])': theme.fn.hoverStyles({ backgroundColor: darken(background, 0.1) }),
    };
};

export interface ButtonStylesParams {
    size: ResponsiveStyleValue<ButtonSize>;
    cornerStyle: ButtonCornerStyle;
    fullWidth: ResponsiveStyleValue<boolean>;
    color: ButtonColor;
    variant: ButtonVariant;
    shape: ButtonShape;
}

type StyleClasses = 'root' | 'icon' | 'loader';

export function useStyles({ size, cornerStyle, fullWidth, color, variant, shape }: ButtonStylesParams): {
    styles: { [key in StyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();
    const { transitions, typography, components } = theme;
    const fontFamily = typography.family.caption;
    const componentTheme = components.themes.Button;

    const root: CSSObject = {
        ...createResponsiveStyles(
            [
                {
                    value: mergeResponsiveStyleValues({ size, fullWidth }, theme),
                    getStyles: getSizeStyles(cornerStyle, shape, theme),
                },
            ],
            theme
        ),
        ...getColorStyles(variant, getElementColors(color, theme), theme),
        ...theme.fn.focusStyles(),
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        lineHeight: 1,
        fontWeight: `${fontFamily.fontWeight[componentTheme.fontWeight]}`,
        textTransform: fontFamily.textTransform,

        borderStyle: 'solid',
        borderWidth: `${componentTheme.borderWidth}px`,

        '&[data-loading]': {
            pointerEvents: 'none',
        },
        transition: `all ${transitions.duration.short}ms ${transitions.easing.easeInOut}`,
    };

    const icon: CSSObject = {
        display: 'inline-block',
        fill: 'currentColor',
    };

    const loader: CSSObject = {
        display: 'inline-block',
        stroke: 'currentColor',
    };

    return {
        styles: {
            root,
            icon,
            loader,
        },
    };
}
